import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { checkModel, slugify, summarise, type ModelInput } from '@motorcity/shared';
import { useAuth } from '../auth/AuthProvider';
import {
  useBrandTree,
  useCreateVariant,
  useDeleteVariant,
  useReorderVariants,
  useSaveModel,
} from '../api/hooks';
import { Breadcrumbs, ErrorNote, PageHeader } from '../components/Layout';
import { LocaleSwitch, countMissing } from '../components/EditLocale';
import { LiveWarning, ReadinessPanel } from '../components/Readiness';
import { EmptyState, FormSkeleton, SectionHeader } from '../components/ui/primitives';
import { errorMessage, useToast } from '../components/ui/Toast';
import { OrderableList } from '../components/OrderableList';
import { SaveBar } from '../components/SaveBar';
import { VehicleForm, emptyVehicle } from '../components/VehicleForm';
import { ColorEditor } from '../components/ColorEditor';
import { MarketingFields } from '../components/MarketingFields';
import { useDraft, useUnsavedWarning } from '../hooks/useDraft';
import { getConfig } from '../config';
import type { ApiError } from '../api/client';

export function ModelPage() {
  const { brandId = '', modelId = '' } = useParams();
  const { user } = useAuth();
  const tree = useBrandTree(brandId);
  const save = useSaveModel(brandId, modelId);
  const reorder = useReorderVariants(brandId, modelId);
  const removeVariant = useDeleteVariant(brandId, modelId);
  const [adding, setAdding] = useState(false);
  const toast = useToast();

  const model = tree.data?.models.find((m) => m.modelId === modelId);
  const serverValue: ModelInput | undefined = model
    ? {
        name: model.name,
        intro: model.intro,
        description: model.description,
        tacticalImage: model.tacticalImage,
        iconFront: model.iconFront,
        iconSide: model.iconSide,
        usps: model.usps,
        startingPrice: model.startingPrice,
        startingEmi: model.startingEmi,
        colors: model.colors ?? [],
        marketing: model.marketing,
        sortOrder: model.sortOrder,
        status: model.status,
      }
    : undefined;

  const { draft, setDraft, dirty, commit, reset } = useDraft(serverValue);
  useUnsavedWarning(dirty);

  if (tree.isLoading || (!tree.error && !draft)) {
    return (
      <div className="page">
        <PageHeader title="Loading…" />
        <FormSkeleton />
      </div>
    );
  }
  if (tree.error) return <ErrorNote error={tree.error} />;
  if (!tree.data || !model || !draft) return <ErrorNote error={new Error('Model not found')} />;

  const missing = countMissing([draft.name, draft.intro, draft.description]);
  const readiness = summarise(checkModel(draft));

  const variants = tree.data.variants
    .filter((v) => v.modelId === modelId)
    .map((variant) => ({
      id: variant.variantId,
      title: variant.name.en,
      subtitle: variant.startingPrice
        ? `${variant.startingPrice.currency} ${variant.startingPrice.amount}`
        : undefined,
      status: variant.status,
      href: `/brands/${brandId}/models/${modelId}/variants/${variant.variantId}`,
      thumbnailUrl: variant.tacticalImage
        ? `${getConfig().contentBaseUrl}/${variant.tacticalImage.key}`
        : undefined,
    }));

  return (
    <div className="page">
      <Breadcrumbs
        trail={[
          { label: 'Brands', to: '/brands' },
          { label: tree.data.brand.name.en, to: `/brands/${brandId}` },
          { label: draft.name.en || modelId },
        ]}
      />

      <PageHeader title={draft.name.en || modelId} slug={modelId}>
        <LocaleSwitch missing={missing} />
      </PageHeader>

      <ReadinessPanel summary={readiness} status={draft.status} entity="model" />

      <VehicleForm
        value={draft}
        requireDescription
        statusWarning={<LiveWarning summary={readiness} />}
        onChange={(next) => setDraft({ ...draft, ...next })}
        assetPrefix={`brands/${brandId}/models/${modelId}`}
        fieldErrors={(save.error as ApiError | undefined)?.fields}
      />

      <MarketingFields
        value={draft.marketing}
        onChange={(marketing) => setDraft({ ...draft, marketing })}
      />

      <ColorEditor
        value={draft.colors ?? []}
        onChange={(colors) => setDraft({ ...draft, colors })}
        assetPrefix={`brands/${brandId}/models/${modelId}`}
      />

      <section className="card">
        <SectionHeader
          title="Variants"
          description="Trim levels under this model. A model with no variants still publishes on its own."
          action={
            <button type="button" className="btn" onClick={() => setAdding(true)}>
              New variant
            </button>
          }
        />

        {adding ? (
          <NewVariantForm brandId={brandId} modelId={modelId} onDone={() => setAdding(false)} />
        ) : null}

        {variants.length === 0 && !adding ? (
          <EmptyState
            title="No variants"
            body="Add trim levels if this model is sold in more than one specification."
            action={
              <button type="button" className="btn primary" onClick={() => setAdding(true)}>
                New variant
              </button>
            }
          />
        ) : (
        <OrderableList
          items={variants}
          canDelete={Boolean(user?.isAdmin)}
          onReorder={(ids) => reorder.mutate(ids)}
          onDelete={(id) => {
            if (window.confirm(`Delete variant "${id}"?`)) {
              removeVariant.mutate(id, {
                onSuccess: () => toast.success(`Deleted ${id}`),
                onError: (err) => toast.error(errorMessage(err)),
              });
            }
          }}
          emptyMessage="No variants yet."
          renderLink={(item, children) => (
            <Link to={item.href} className="orderable-link">
              {children}
            </Link>
          )}
        />
        )}
      </section>

      <SaveBar
        dirty={dirty}
        saving={save.isPending}
        error={save.error}
        onReset={reset}
        onSave={() =>
          save.mutate(draft, {
            onSuccess: () => {
              commit(draft);
              toast.success('Model saved');
            },
            onError: (err) => toast.error(errorMessage(err)),
          })
        }
      />
    </div>
  );
}

function NewVariantForm({
  brandId,
  modelId,
  onDone,
}: {
  brandId: string;
  modelId: string;
  onDone: () => void;
}) {
  const create = useCreateVariant(brandId, modelId);
  const toast = useToast();
  const [nameEn, setNameEn] = useState('');
  const variantId = slugify(nameEn);

  return (
    <div className="inline-form">
      <div className="row">
        <input
          className="input"
          placeholder="Variant name (English)"
          autoFocus
          value={nameEn}
          onChange={(e) => setNameEn(e.target.value)}
        />
        <code className="slug">{variantId || '—'}</code>
        <button type="button" className="btn subtle" onClick={onDone}>
          Cancel
        </button>
        <button
          type="button"
          className="btn primary"
          data-loading={create.isPending || undefined}
          disabled={variantId.length < 2 || create.isPending}
          onClick={() =>
            create.mutate(
              { ...emptyVehicle(), variantId, name: { en: nameEn.trim(), ar: '' } },
              {
                onSuccess: (variant) => {
                  toast.success(`Created ${variant.name.en}`);
                  onDone();
                },
                onError: (err) => toast.error(errorMessage(err)),
              },
            )
          }
        >
          Create
        </button>
      </div>
      {create.error ? <ErrorNote error={create.error} /> : null}
    </div>
  );
}
